import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import http from '@/services/http';

/**
 * The full tag catalog (GET /api/tag) as one cached lookup.
 *
 * Tags embed in search documents as bare {id, display_string}, but their
 * presentation needs the catalog flags: is_track_user_tag / is_artist_tag
 * drive the badge font color, parent_tag_id resolves the header ("group")
 * tag for tooltips and for grouping the tag filter options, and track_tag
 * scopes which tags a given entity's filter offers (Live Tracks filter on
 * track tags, every other entity on non-track tags). One query serves all
 * of those consumers.
 */

export interface CatalogTag {
    id: number;
    name: string;
    track_tag: boolean;
    parent_tag_id: number | null;
    is_header_tag: boolean;
    is_track_user_tag: boolean;
    is_artist_tag: boolean;
}

/** Badge/font kind derived from the catalog flags (user wins over artist). */
export type TagKind = 'track' | 'artist' | 'user';

export function tagKind(tag: Pick<CatalogTag, 'is_track_user_tag' | 'is_artist_tag'>): TagKind {
    if (tag.is_track_user_tag) return 'user';
    if (tag.is_artist_tag) return 'artist';
    return 'track';
}

export async function fetchTagCatalog(): Promise<CatalogTag[]> {
    const response = await http.get('/api/tag');
    const rows = (response.data as { data?: unknown[] }).data ?? [];
    return rows.filter((row): row is CatalogTag => !!row && typeof row === 'object');
}

/** Tags change rarely; keep one catalog per session unless explicitly invalidated. */
const TAG_CATALOG_STALE_MS = 5 * 60 * 1000;

export function useTagCatalog() {
    const query = useQuery({
        queryKey: ['tag-catalog'],
        queryFn: fetchTagCatalog,
        staleTime: TAG_CATALOG_STALE_MS,
        retry: 1,
    });

    const tags = computed<CatalogTag[]>(() => query.data.value ?? []);
    const tagsById = computed(() => {
        const byId = new Map<number, CatalogTag>();
        for (const tag of tags.value) {
            byId.set(tag.id, tag);
        }
        return byId;
    });

    /** The header/group tag's name for a child tag, or null for root tags. */
    function parentTagName(tag: CatalogTag | undefined): string | null {
        if (!tag || tag.parent_tag_id == null) return null;
        return tagsById.value.get(tag.parent_tag_id)?.name ?? null;
    }

    return { tags, tagsById, parentTagName, isLoading: query.isLoading };
}
